import { useCallback } from 'react';
import { useEntityFilters } from '../ui/useEntityFilters';
import type { Blueprint, BlueprintStatus } from '@/lib/types';

const STORAGE_KEY = 'blueprint-filters';

export type BlueprintStatusFilter = 'building' | 'ready' | 'failed' | null;

interface StoredFilters {
  status?: BlueprintStatusFilter;
  search?: string;
}

export const BUILDING_STATUSES: BlueprintStatus[] = ['queued', 'provisioning', 'building'];
const READY_STATUSES: BlueprintStatus[] = ['build_complete'];
const FAILED_STATUSES: BlueprintStatus[] = ['failed'];

const STATUS_GROUP: Record<string, BlueprintStatus[]> = {
  building: BUILDING_STATUSES,
  ready: READY_STATUSES,
  failed: FAILED_STATUSES,
};

export interface BlueprintFilters {
  status: BlueprintStatusFilter;
  search: string;
}

export interface BlueprintFiltersResult {
  filters: BlueprintFilters;
  setStatus: (status: BlueprintStatusFilter) => void;
  setSearch: (search: string) => void;
  filterBlueprints: (blueprints: Blueprint[]) => Blueprint[];
}

const BLUEPRINT_DEFAULTS: StoredFilters = { status: null, search: '' };

const getBlueprintStatus = (bp: Blueprint): BlueprintStatus => bp.status;
const getStatusFilter = (f: BlueprintFilters): string | null => f.status;

function normalizeBlueprintFilters(
  stored: StoredFilters,
  defaults: StoredFilters,
): BlueprintFilters {
  return {
    status: stored.status ?? defaults.status ?? null,
    search: stored.search ?? defaults.search ?? '',
  };
}

function blueprintExtraFilter(blueprints: Blueprint[], filters: BlueprintFilters): Blueprint[] {
  if (!filters.search) return blueprints;
  const needle = filters.search.toLowerCase();
  return blueprints.filter((bp) => bp.name.toLowerCase().includes(needle));
}

export function useBlueprintFilters(): BlueprintFiltersResult {
  const { filters, setField, filterItems } = useEntityFilters<
    StoredFilters,
    BlueprintFilters,
    Blueprint,
    BlueprintStatus
  >({
    storageKey: STORAGE_KEY,
    defaults: BLUEPRINT_DEFAULTS,
    normalize: normalizeBlueprintFilters,
    statusGroupMap: STATUS_GROUP,
    getStatusFilter: getStatusFilter,
    getItemStatus: getBlueprintStatus,
    extraFilter: blueprintExtraFilter,
  });

  const setStatus = useCallback(
    (status: BlueprintStatusFilter) => setField('status', status),
    [setField],
  );

  const setSearch = useCallback((search: string) => setField('search', search), [setField]);

  return { filters, setStatus, setSearch, filterBlueprints: filterItems };
}
